import React from 'react';
interface GeldhausLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'full' | 'icon';
}
export const GeldhausLogo = ({
  className = '',
  size = 'md',
  variant = 'full'
}: GeldhausLogoProps) => {
  const sizes = {
    sm: {
      container: 'h-10',
      text: 'text-xl',
      icon: 40,
      tagline: 'text-[9px]'
    },
    md: {
      container: 'h-14',
      text: 'text-2xl',
      icon: 56,
      tagline: 'text-[10px]'
    },
    lg: {
      container: 'h-20',
      text: 'text-4xl',
      icon: 80,
      tagline: 'text-xs'
    }
  };
  const sizeClasses = sizes[size];
  return <div className={`flex items-center gap-3 ${className}`} data-id="element-748">
      <div className={`${sizeClasses.container} aspect-square relative group`} data-id="element-749">
        <svg width={sizeClasses.icon} height={sizeClasses.icon} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-xl" role="img" aria-label="Geldhaus logo" data-id="element-750">
          <title data-id="element-751">Geldhaus Logo</title>
          <defs data-id="element-752">
            <linearGradient id="primaryGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-753">
              <stop offset="0%" stopColor="#3d8b68" data-id="element-754" />
              <stop offset="50%" stopColor="#2f7556" data-id="element-755" />
              <stop offset="100%" stopColor="#2d6b4f" data-id="element-756" />
            </linearGradient>

            <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-757">
              <stop offset="0%" stopColor="#fbbf24" data-id="element-758" />
              <stop offset="100%" stopColor="#f59e0b" data-id="element-759" />
            </linearGradient>

            <radialGradient id="gloss" cx="35%" cy="35%" data-id="element-760">
              <stop offset="0%" stopColor="white" stopOpacity="0.25" data-id="element-761" />
              <stop offset="100%" stopColor="white" stopOpacity="0" data-id="element-762" />
            </radialGradient>
          </defs>

          <circle cx="40" cy="40" r="38" fill="white" stroke="#e5e7eb" strokeWidth="2" data-id="element-763" />

          <g opacity="0.7" data-id="element-764">
            <rect x="8" y="58" width="5" height="14" fill="url(#successGradient)" rx="2" data-id="element-765" />
            <rect x="18" y="52" width="5" height="20" fill="url(#successGradient)" rx="2" data-id="element-766" />
            <rect x="28" y="44" width="5" height="28" fill="url(#successGradient)" rx="2" data-id="element-767" />
            <rect x="38" y="36" width="5" height="36" fill="url(#successGradient)" rx="2" data-id="element-768" />
            <rect x="48" y="26" width="5" height="46" fill="url(#successGradient)" rx="2" data-id="element-769" />
            <rect x="58" y="18" width="5" height="54" fill="url(#successGradient)" rx="2" data-id="element-770" />

            <path d="M 10 60 L 20 54 L 30 46 L 40 38 L 50 28 L 60 20" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-771" />

            <path d="M 57 22 L 60 20 L 58 17" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-772" />

            <path d="M 64 16 L 66 14 L 68 16" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-773" />
          </g>

          {/* Rotating Fan - respects prefers-reduced-motion */}
          <g className="origin-center motion-safe:animate-spin-slow" style={{
          transformBox: 'fill-box'
        }} data-id="element-774">
            <g transform="rotate(0 40 40)" data-id="element-775">
              <path d="M 40 40 Q 45 25, 52 18 L 56 22 Q 48 30, 43 44 Z" fill="url(#primaryGradient)" stroke="#047857" strokeWidth="1" data-id="element-776" />
              <circle cx="48" cy="28" r="3" fill="url(#successGradient)" data-id="element-777" />
              <circle cx="50" cy="32" r="1.5" fill="url(#successGradient)" opacity="0.7" data-id="element-778" />
            </g>

            <g transform="rotate(90 40 40)" data-id="element-779">
              <path d="M 40 40 Q 45 25, 52 18 L 56 22 Q 48 30, 43 44 Z" fill="#047857" stroke="#065f46" strokeWidth="1" data-id="element-780" />
              <circle cx="48" cy="28" r="3" fill="url(#successGradient)" data-id="element-781" />
              <circle cx="50" cy="32" r="1.5" fill="url(#successGradient)" opacity="0.7" data-id="element-782" />
            </g>

            <g transform="rotate(180 40 40)" data-id="element-783">
              <path d="M 40 40 Q 45 25, 52 18 L 56 22 Q 48 30, 43 44 Z" fill="url(#primaryGradient)" stroke="#047857" strokeWidth="1" data-id="element-784" />
              <circle cx="48" cy="28" r="3" fill="url(#successGradient)" data-id="element-785" />
              <circle cx="50" cy="32" r="1.5" fill="url(#successGradient)" opacity="0.7" data-id="element-786" />
            </g>

            <g transform="rotate(270 40 40)" data-id="element-787">
              <path d="M 40 40 Q 45 25, 52 18 L 56 22 Q 48 30, 43 44 Z" fill="#047857" stroke="#065f46" strokeWidth="1" data-id="element-788" />
              <circle cx="48" cy="28" r="3" fill="url(#successGradient)" data-id="element-789" />
              <circle cx="50" cy="32" r="1.5" fill="url(#successGradient)" opacity="0.7" data-id="element-790" />
            </g>

            <circle cx="40" cy="40" r="7" fill="url(#successGradient)" stroke="#d97706" strokeWidth="1.5" data-id="element-791" />

            <circle cx="40" cy="40" r="4" fill="white" opacity="0.3" data-id="element-792" />
          </g>

          <g opacity="0.25" data-id="element-793">
            <path d="M 68 38 L 73 38" stroke="#10b981" strokeWidth="2" strokeLinecap="round" className="motion-safe:animate-pulse" data-id="element-794" />
            <path d="M 70 41 L 75 41" stroke="#10b981" strokeWidth="2" strokeLinecap="round" className="motion-safe:animate-pulse" style={{
            animationDelay: '0.2s'
          }} data-id="element-795" />
            <path d="M 68 44 L 73 44" stroke="#10b981" strokeWidth="2" strokeLinecap="round" className="motion-safe:animate-pulse" style={{
            animationDelay: '0.4s'
          }} data-id="element-796" />
          </g>
        </svg>
      </div>

      {variant === 'full' && <div className="flex flex-col leading-none" data-id="element-797">
          <div className="flex items-baseline" data-id="element-798">
            <span className={`${sizeClasses.text} font-bold text-gray-900 tracking-tight`} data-id="element-799">
              Geldhaus
            </span>
          </div>
          <span className={`${sizeClasses.tagline} text-primary-600 font-bold uppercase tracking-[0.15em] -mt-0.5`} data-id="element-800">
            Your Growth Partner
          </span>
        </div>}
    </div>;
};